import { useState, useEffect, useRef, useImperativeHandle, forwardRef, useContext  } from "react";
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import FiltersTable from '../../../components/FiltersTable';
import { Link } from 'react-router-dom';
import { roles } from "../../../constants/roles";
import useToken from "../../../hooks/useToken";
import { getTableData } from "./Services";
import { generateFilterPayload, transformData, resolveSearchFilters } from "../../../components/FilterUtils";
import { convertToInt, formatNumber } from "../../common";
import { commonStrings } from "../../../components/StringBundle";
import moment from "moment/moment";
import Snackbar from "../../../components/Snackbar";
import { filterTableSorter } from "../../../components/TableCustomSort";
import { AuthContext } from "../../../context/auth-context";

const useStyles = makeStyles({
  root: {
    width: '100%',
    marginTop: 24,
  },
  container: {
    maxHeight: 440,
  },
});

const QLIReportList = forwardRef((props, ref) => {

  const { currentState, pickListValue, company } = props;
  const classes = useStyles();
  const { getCurrentUserDetails } = useToken();
  let currentUserDetails = getCurrentUserDetails();
  const auth = useContext(AuthContext);
  const companyName = auth?.company === "ncl" ? commonStrings.ncl : commonStrings.ccl;
  const userRole = currentUserDetails?.role;
  const savedFilter = JSON.parse(sessionStorage.getItem('FiltersTable./QLI-Reports'));

  const [allData, setAllData] = useState([]);
  const [pageSize, setPageSize] = useState(sessionStorage.getItem('selectedpagesize') || 10);
  const [searchFilters, setSearchFilters] = useState(savedFilter || []);
  const [totalrecord, setTotalrecord] = useState(0);
  const [showSnack, setSnack] = useState({ open: false, message: '', severity: '' });
  const [columns, setColumns] = useState([]);
  const [page, setPage] = useState(sessionStorage.getItem('selectedpagenumber') || 1);


  //  --- On unmount: Clean up sessionStorage ---
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('FiltersTable./QLI-Reports');
      sessionStorage.removeItem('selectedQliCategory');
      sessionStorage.removeItem('qliviewpicklist');
      sessionStorage.removeItem('selectedpagenumber');
      sessionStorage.removeItem('selectedpagesize');
    }
  }, []);

  // Fetch data when searchFilters, page, pageSize, currentState, viewpicklist change
  useEffect(() => {
    if (currentState) {
      getData(searchFilters, page, pageSize);
    }
  }, [searchFilters, page, pageSize, currentState, pickListValue, company]);

  //  Use useImperativeHandle to expose methods to parent
  useImperativeHandle(ref, () => ({
    getExportParams: () => ({
      totalrecord,
      filters: searchFilters,
    }),
    reset: () => {
      setSearchFilters([]);
      setPage(1);
    },
    resetPage: () => {
      setPage(1);
    },
  }));

  // Render functions for each column
  const renderFunctions = {
    quote_number: (text, record) => {
      const url = `/quotation/${record.quote_id}/view?status=${record.status_id}&state=${record.status_id}`;
      return userRole === roles.productionplanning ? (
        <span>{text}</span>
      ) : (
        <Link to={url}>{text}</Link>
      );
    },
    order_number: (text, record) => (
      <Link to={`/order/${record.order_id}/view?tab=order`}>{text}</Link>
    ),
    order_dispatch_number: (text, record) => (
      <Link to={`/orderdispatch/${record.order_dispatch_id}/view?tab=orderdispatch`}>
        {text}
      </Link>
    ),
    soft_schedule_no: (text, record) => {
      const url = `/soft-scheduling/${encodeURIComponent(record.order_dispatch_number)}/view?tab=soft-schedule`;
      return [roles.BDManager, roles.BusinessDevelopmentHod, roles.BusinessDevelopmentExecutive, roles.BDDomestic, roles.BusinessDevelopmentManager].includes(userRole) ? (
        <span>{text}</span>
      ) : (
        <Link to={url}>{text}</Link>
      );
    },
  };

  // Build payload
  const buildPayload = (searchFilterss, page, newsize) => {
    const searchedValues = resolveSearchFilters(searchFilterss, searchFilters);
    const updatedSearchValue = [...searchedValues];
    const existingFilter = updatedSearchValue.find(v => v.searchedColumn === 'quote_category');

    if (existingFilter) {
      existingFilter.updatedSearchValue = currentState;
    } else {
      updatedSearchValue.push({
        searchText: currentState,
        searchedColumn: 'quote_category',
        searchedTitle: 'Quote Category'
      });
    }
    return transformData(generateFilterPayload(updatedSearchValue, {
      request_type: pickListValue,
      page: convertToInt(page),
      limit: convertToInt(newsize ? newsize : pageSize),
      company_name: company,
    }));
  };

  // Fetch data
  const getData = async (searchFilterss, page, newsize) => {
    const payload = buildPayload(searchFilterss, page, newsize);
    setAllData(null);

    try {
      const response = await getTableData(payload);
      const data = response?.report_details || [];
      const keyPairs = response?.report_key_pair || [];
      const columnTypeMap = {
        created_date: "date"
      };
      const columns = keyPairs.map((item, index) => {
        const itemId = item?.id;
        const type = columnTypeMap[itemId];
        const columnDef = {
          title: item?.label,
          dataIndex: itemId,
          key: itemId || `column-${index}`,
          width: '15%',
        };

        if (renderFunctions[itemId]) {
          columnDef.render = renderFunctions[itemId];
        }

        // Only assign sorter if explicitly defined AND not excluded
        if (type) {
          let format = undefined;
          if(type === 'date') {
            format = 'DD-MM-YYYY';
          }
          columnDef.sorter = filterTableSorter(itemId, type, format);
        }

        return columnDef;
      });
      setColumns(columns);
      let rows = [];
      data.forEach((v) => {
        rows.push({
          ...v,
          confirmed_order_quantityKgs: formatNumber(v.confirmed_order_quantityKgs, 3),
          scheduled_quantity: formatNumber(v.scheduled_quantity, 3),
          balance_quantity: formatNumber(v.balance_quantity, 3),
        });
      });
      setAllData(rows);
      setTotalrecord(response?.total_count);
    } catch (err) {
      setSnack({
        open: true,
        message: commonStrings.serverError,
        severity: commonStrings.error,
      });
      setAllData([]);
      setTotalrecord(0);
    }
  };

  // Table Search function
  const filterDataColumn = (searchArr) => {
    let filters = [];
    if (searchArr === '') {
      filters = [];
    } else if (searchArr && searchArr.length > 0) {
      filters = searchArr;
    } else {
      filters = searchFilters || [];
    }
    setSearchFilters(filters);
    setPage(1);
  };

  // Pagination function
  const onPaginationChange = (newPage, newSize) => {
    setPage(newPage);
    setPageSize(newSize);
  }

  return (
    <>
      {showSnack.open && <Snackbar {...showSnack} handleClose={() => setSnack({ open: false, message: '', severity: '' })} />}
      <Paper className={classes.qliroot}>
        <FiltersTable
          columns={columns}
          tableData={allData}
          totalrecord={totalrecord}
          filterDataColumn={filterDataColumn}
          apiFilter={true}
          pagination={page}
          onPaginationChange={onPaginationChange}
        />
      </Paper>
    </>
  );
});

export default QLIReportList;